import { useEffect, useState, useRef, useCallback } from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Typography from "@mui/material/Typography";
import { Divider,useMediaQuery } from "@mui/material";

export default function ImageGallery() {
  const [images, setImages] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const dialogRef = useRef(null);
  const touchStartX = useRef(null);
  const isMobile = useMediaQuery('(min-width: 600px)');
  const height = isMobile ? "50%" : "100%"

  useEffect(() => {
    const fetchingImages = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/showimages`);
        const jsonData = await response.json();
        const temp = jsonData.map((image) => ({
          img: `${image.image}`,
          title: image.image_name,
        }));
        setImages(temp);
      } catch (error) {
        console.log(error);
      }
    };
    fetchingImages();
  }, []);

  const handleClickOpen = (index) => {
    setSelectedIndex(index);
    setOpen(true);
  };

  const handleClose = (_, reason) => {
    // Prevent closing on backdrop click or escape key
    if (reason === "backdropClick" ) return;
    setOpen(false);
    setSelectedIndex(null);
  };

  const handlePrev = useCallback(() => {
    setSelectedIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : images.length - 1));
  }, [images.length]);

  const handleNext = useCallback(() => {
    setSelectedIndex((prevIndex) => (prevIndex < images.length - 1 ? prevIndex + 1 : 0));
  }, [images.length]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "ArrowLeft") handlePrev();
      if (event.key === "ArrowRight") handleNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handlePrev, handleNext]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    if (touchStartX.current === null) return;
    const diffX = e.touches[0].clientX - touchStartX.current;
    if (diffX > 50) {
      handlePrev();
      touchStartX.current = null;
    } else if (diffX < -50) {
      handleNext();
      touchStartX.current = null;
    }
  };

  return (
    <Box sx={{ marginTop: { md: 20, xs: 10 } }}>
      <Box sx={{ display: { md: "none", xs: "" }, p: 2 }}>
        <Divider sx={{ '--Divider-childPosition': '50%', fontWeight: 200, fontSize: 16 }}>
          Gallery
        </Divider>
      </Box>

      <Box
        sx={{
          position: "relative",
          display: "flex",
          width: "100%",
          flexWrap: { md: 'nowrap', xs: 'wrap' },
          justifyContent: "center",
          paddingLeft: 2,
          paddingRight: 2,
          border: { md: "1px solid", xs: "" },
          borderColor: { md: "divider", xs: "" },
          borderRadius: "10px",
        }}
      >
        <Typography
          variant='h6'
          align='center'
          sx={{
            display: { md: "flex", xs: "none" },
            position: "absolute",
            top: -16,
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "white",
            padding: "0 10px",
            fontWeight: 200,
            fontSize: 18,
          }}
        >
          Gallery
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            width: "100%",
            margin: "auto",
            gap:  {md: 3, xs: 2},
            p: {md: 3, xs: 1}
          }}
        >
          {images.map((item, index) => (
            <Box
              key={index}
              onClick={() => handleClickOpen(index)}
              sx={{
                flex: "0 1 calc(33.33% - 16px)",
                cursor: "pointer",
                textAlign: "center",
                "&:hover img": {
                  transform: "scale(1.05)",
                  transition: "transform 0.3s ease-in-out",
                },
              }}
            >
              <img
                src={item.img}
                alt={item.title}
                style={{
                  width: "100%",
                  height: isMobile ? "170px" : "95px",
                  objectFit: "cover",
                  borderRadius: "10px",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                }}
              />
              {/* <Typography
                variant="subtitle2"
                sx={{
                  fontSize: { xs: 10, md: null },
                  fontWeight: "bold",
                  color: "text.primary",
                  paddingBottom: 3
                }}
              >
                {item.title}
              </Typography> */}
            </Box>
          ))}
        </Box>

        <Dialog
          open={open}
          onClose={handleClose}
          fullWidth
          maxWidth="md"
          ref={dialogRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          PaperProps={{
            sx: {
              backgroundColor: "transparent",
              boxShadow: "none",
            }
          }}
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.4)",
            }
          }}
        >
          <Box
            sx={{
              textAlign: "center",
              padding: 1,
              gap: 0.5,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <IconButton
              onClick={() => handleClose(null, null)}
              sx={{
                alignSelf: "flex-end",
                backgroundColor: "rgba(255,255,255,0.7)",
                
                "&:hover": { backgroundColor: "rgba(255,255,255,0.9)" },
              }}
            >
              <CloseIcon sx={{ fontSize: 26 }} />
            </IconButton>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", gap: 2}}>
              <IconButton
                onClick={handlePrev}
                sx={{
                  display: {md: "flex", xs: "none"},
                  backgroundColor: "rgba(255,255,255,0.7)",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.9)" },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 16 }} />
              </IconButton>

              {selectedIndex !== null && (
                <img
                  src={images[selectedIndex].img}
                  alt={images[selectedIndex].title}
                  style={{
                    maxWidth: "90%",
                    maxHeight: "70vh",
                    borderRadius: "8px",
                    boxShadow: "0 6px 12px rgba(0,0,0,0.3)"
                  }}
                  
                />
              )}
              
              <IconButton
                onClick={handleNext}
                sx={{
                  display: {md: "flex",  xs: "none"},
                  backgroundColor: "rgba(255,255,255,0.7)",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.9)" },
                }}
              >
                <ArrowForwardIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box> 

            {selectedIndex !== null && (
              <Box  sx={{ backgroundColor: "rgba(255, 255, 255, 0.95)", borderRadius: "8px", p: 0.5, width: {sm: 270, xs: 270, md: 500}}}>
                 <Typography  sx={{
                  fontSize: 12,
                  display: "block",
                  whiteSpace: "normal", 
                  // wordWrap: "break-word", 
                  }} >
                {images[selectedIndex].title}
              </Typography>
              </Box>
             
            )}
          </Box>
        </Dialog>
      </Box>
    </Box>
  );
}
